import {useEffect, useRef, useState} from "react";
import {useDispatch} from "react-redux";
import {Marker, Popup, useMap, useMapEvents} from "react-leaflet";
import Geocode from "react-geocode";
import {mapCoordinates} from "../../reduxToolkit/UpdateCompany/UpdateCompanySlice";
import L from "leaflet";
import ReactDOM from "react-dom";
import {GEO_API} from "../../Apis/Constants";
import MarkerIcon from 'leaflet/dist/images/marker-icon.png';
import {useTranslation} from "react-i18next";
/**
 *
 * AddMarkerToClick Component
 *
 * Utility component for React Leaflet that allows adding a **marker** to the map
 * when the user clicks on it. It updates latitude and longitude state,
 * performs reverse geocoding to get the address, and dispatches the coordinates
 * to the Redux store.
 *
 * Supports optional clustering for multiple markers and custom popup content.
 *
 * @author Ferro Zapata Arnulfo
 * @email azapata@ibl.mx
 * @jobTitle Full Stack Developer
 *
 * @param {Object} props - Component props
 * @param {Array<Object>} [props.markersList=[]] - Initial markers to render
 * @param {Function} props.setLatitude - Function to update the selected latitude
 * @param {Function} props.setLongitude - Function to update the selected longitude
 * @param {boolean} [props.disabled=false] - If true, disables click events
 * @param {React.ReactNode} [props.customMarkerPopup=false] - Custom content for marker popups
 * @param {L.Icon} [props.iconMarker] - Custom Leaflet icon for markers
 * @param {Function} [props.onMarkerClick] - Callback for marker click event
 * @param {Function} [props.onMarkerHover] - Callback for marker hover event
 * @param ifCluster - boolean to assign cluster markers or solo marker
 */

export function AddMarkerToClick({markersList = [], setLatitude, setLongitude, disabled = false,
                              customMarkerPopup = false, iconMarker, onMarkerClick, onMarkerHover,
                                     latlng=[] , ifCluster=false})
{
    const [markers, setMarkers] = useState(markersList);
    const dispatch = useDispatch();
    const map = useMap();
    const clusterGroupRef = useRef(null);
    const {t} = useTranslation();
    const defaultLeafletIcon = iconMarker ?
        iconMarker :
        new L.Icon({
            iconUrl: MarkerIcon,
            iconSize: [25, 41],
            iconAnchor: [12, 41],
            popupAnchor: [1, -34],
            tooltipAnchor: [16, -28],
            shadowSize: [41, 41],
        });

    useEffect(() => {
        if (ifCluster) {
            if (markersList.length <= 1) {
                const isArrayOfArrays = Array.isArray(markersList[0]) && Array.isArray(latlng);
                let isSame = false;
                if (isArrayOfArrays) {
                    isSame = markersList.some(m =>
                        m.every((value, index) => value === latlng[index])
                    );
                } else {
                    isSame = markersList.some(m =>
                        Array.isArray(m.position) &&
                        m.position.every((value, index) => value === latlng[index])
                    );
                }

                if (isSame) {
                    setMarkers([]);
                } else {
                    setMarkers(markersList);
                }
            } else {
                setMarkers(markersList);
            }
        }
        if (
            markersList.length === 1 &&
            latlng &&
            markersList[0]?.position?.[0] === latlng[0] &&
            markersList[0]?.position?.[1] === latlng[1]
        ) return;
    }, [markersList, latlng]);


    useMapEvents({
        click(e) {
            if (disabled) return;
            const { lat, lng } = e.latlng;
            setMarkers([{ position: [lat, lng] }]);
            setLatitude(lat);
            setLongitude(lng);

            Geocode.setApiKey(GEO_API);
            Geocode.fromLatLng(lat, lng)
                .then((response) => {
                    dispatch(
                        mapCoordinates({
                            address: response.results[0].formatted_address,
                            lat,
                            lng,
                        })
                    );
                })
                .catch((error) => console.error(error));
        },
    });

    const createClusterGroup = () => {
        return L.markerClusterGroup({
            showCoverageOnHover: false,
            spiderfyDistanceMultiplier: 1.5,
            chunkedLoading: true,
        });
    };

    useEffect(() => {
        if (!map || !Array.isArray(markers)) return;

        if (clusterGroupRef.current) {
            clusterGroupRef.current.eachLayer((layer) => {
                if (layer.getPopup?.()) {
                    const popupElement = layer.getPopup().getElement();
                    if (popupElement) {
                        ReactDOM.unmountComponentAtNode(popupElement);
                    }
                }
            });
            if (map.hasLayer(clusterGroupRef.current)) {
                map.removeLayer(clusterGroupRef.current);
            }
            clusterGroupRef.current = null;
        }

        if (markers.length > 1) {
            const markerClusterGroup = createClusterGroup();
            clusterGroupRef.current = markerClusterGroup;

            markers.forEach((marker) => {
                const position = marker.position || marker;
                const leafletMarker = L.marker(position, {
                    icon: iconMarker || defaultLeafletIcon,
                });

                if (onMarkerClick) {
                    leafletMarker.on('click', () => {
                        if (marker) onMarkerClick(marker); else onMarkerClick();
                    });
                } else {
                    leafletMarker.bindPopup(() => {
                        const container = document.createElement("div");
                        const content =
                            marker.customPopup ||
                            customMarkerPopup ||
                            <p>Marker at {position.toString()}</p>;
                        ReactDOM.render(content, container);
                        return container;
                    });
                }

                if (onMarkerHover) {
                    leafletMarker.on('mouseover', () => {
                        onMarkerHover(marker, leafletMarker);
                    });
                    leafletMarker.on('mouseout', () => {
                        leafletMarker.closeTooltip();
                    });
                }

                markerClusterGroup.addLayer(leafletMarker);
            });

            map.addLayer(markerClusterGroup);
        }

        return () => {
            if (clusterGroupRef.current) {
                clusterGroupRef.current.eachLayer((layer) => {
                    if (layer.getPopup?.()) {
                        const popupElement = layer.getPopup().getElement();
                        if (popupElement) {
                            ReactDOM.unmountComponentAtNode(popupElement);
                        }
                    }
                });
                if (map.hasLayer(clusterGroupRef.current)) {
                    map.removeLayer(clusterGroupRef.current);
                }
                clusterGroupRef.current = null;
            }
        };
    }, [map, markers, onMarkerClick, onMarkerHover, iconMarker, customMarkerPopup]);

    if (markers.length <= 1) {
        return (
            <>
                {markers.map((marker, index) => {
                    const position = marker.position || marker;
                    const popupContent =  marker.customPopup || customMarkerPopup || <p>{t("market_at")} {position.toString()}</p>;

                    return (
                        <Marker
                            key={index}
                            position={position}
                            icon={iconMarker || defaultLeafletIcon}
                            eventHandlers={{
                                click: () => {
                                    if (onMarkerClick) {
                                        if (marker) onMarkerClick(marker);
                                        else onMarkerClick();
                                    }
                                },
                                mouseover: (e) => {
                                    if (onMarkerHover) {
                                        const leafletElement = e.target;
                                        onMarkerHover(marker, leafletElement);
                                    }
                                },
                                mouseout: (e) => {
                                    e.target.closeTooltip();
                                }
                            }}
                        >
                            {!onMarkerClick && <Popup>{popupContent}</Popup>}
                        </Marker>
                    );
                })}
            </>
        );
    }
    return null;
}

/**
 * SearchMap Component
 *
 * React Leaflet utility component that centers and zooms the map
 * based on a given address string. It uses Google Geocoding API to
 * transform an address into latitude and longitude.
 *
 * When a valid location is found, the map animates smoothly to it.
 *
 *
 * @param {Object} props - Component props
 * @param {string} props.searchLocation - The address to geocode and locate on the map
 * @param {Function} props.setCenter - Function to update map center coordinates
 * @param {Function} props.setZoom - Function to update map zoom level
 *
 * @returns {null} Does not render UI; only performs map actions
 *
 * @author Arnulfo Ferro Zapata
 * @createdAt 2025-10-07
 */
export function SearchMap ({ searchLocation = '', setCenter, setZoom }) {
    const map = useMap();

    useEffect(() => {
        if (!map || searchLocation === '') return;
        if (searchLocation !== '') {
            Geocode.setApiKey(GEO_API);
            Geocode.fromAddress(searchLocation).then((response) => {
                const { lat, lng } = response?.results[0]?.geometry?.location;
                setCenter([lat, lng]);
                setZoom(12);
                map.flyTo([lat, lng], 12, { duration: 2 });
            });
        }
    }, [searchLocation, setCenter, setZoom]);

    return null;
}


/**
 * React component that listens to Leaflet map events (`moveend` and `zoomend`)
 * and triggers a callback with the map's current center and zoom level.
 *
 * This component does not render any UI elements; it only attaches event listeners
 * to the map using `useMapEvents` from React Leaflet.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {function} [props.onMapMove] - Optional callback function triggered when the map finishes moving or zooming.
 * The function receives three parameters: latitude, longitude, and zoom level.
 *
 * @example
 * <MapContainer center={[20.5888, -100.3899]} zoom={13}>
 *   <MapEventHandler onMapMove={(lat, lng, zoom) => console.log(lat, lng, zoom)} />
 * </MapContainer>
 */
export const MapEventHandler = ({ onMapMove }) => {
    useMapEvents({
        moveend: (event) => {
            const map = event.target;
            const center = map.getCenter();
            const zoom = map.getZoom();
            onMapMove?.(center.lat, center.lng, zoom);
        },
        zoomend: (event) => {
            const map = event.target;
            const center = map.getCenter();
            const zoom = map.getZoom();
            onMapMove?.(center.lat, center.lng, zoom);
        },
    });
    return null;
};
